/**
 * SimplifiedRegisterSheet
 * ───────────────────────
 * Mar 2026 — drop-in replacement for the matrix-style picker behind the
 * REACT_APP_SIMPLIFIED_CAMP_FLOW feature flag.
 *
 * One decision per parent: **Half day AM / Half day PM / Full day** — that's
 * it. No daily checkboxes, no per-session toggles. We POST the picked
 * `format` token to /api/camps/{id}/add-to-cart/ and let the backend expand
 * it into the existing `selected_sessions[]` shape.
 *
 * Behavior contract:
 *   • K-2 sections render only the "Half day · Mornings" option (Full day
 *     hidden — K-2 camps run AM only).
 *   • Extended care only appears for Full-day at independent weekday
 *     locations (Naperville / Aurora / Oswego / Plainfield) — same rule
 *     enforced server-side; we hide here so the parent doesn't pick an EC
 *     option that 400s on submit.
 *   • Multiple learners can be ticked; one POST per learner runs in
 *     parallel; toast aggregates the result.
 *   • Total updates live based on price_5_day and price_5_day_halfday
 *     fields surfaced on the section payload (admin-editable).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Clock, Sunrise, Users, Sparkles, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const FORMAT_LABELS = {
  weekly_am: { title: 'Half day · Mornings', time: '9 AM – 12 PM' },
  weekly_pm: { title: 'Half day · Afternoons', time: '1 PM – 4 PM' },
  full_week: { title: 'Full day', time: '9 AM – 4 PM · lunch included' },
};

const EC_OPTIONS = [
  { value: 'none', label: 'None', sub: '9 AM – 4 PM' },
  { value: 'early', label: 'Early drop-off', sub: '8 AM – 9 AM · +$50' },
  { value: 'late', label: 'Late pick-up', sub: '4 PM – 5 PM · +$50' },
  { value: 'both', label: 'Both', sub: 'Early + Late · +$100' },
];

function isExtCareEligible(section) {
  const name = section?.location_name || '';
  if (name.includes('Bednarcik') || name.includes('Plank')) return false;
  if (name.includes('YMCA')) return false;
  if (/weekend/i.test(name)) return false;
  return /Naperville|Aurora|Oswego|Plainfield/i.test(name);
}

export default function SimplifiedRegisterSheet({
  open,
  onClose,
  camp,
  section,
  learners = [],
  defaultFormat,
  onAddedToCart,
}) {
  const { user, api } = useAuth();
  const isK2 = (camp?.grade_bands || []).some(g => {
    const u = String(g).toUpperCase();
    return ['K', '1', '2', 'K-2', 'K–2'].includes(u);
  });

  const gradeMatchesBands = (learnerGrade, gradeBands) => {
    if (!gradeBands || gradeBands.length === 0) return true;
    const g = String(learnerGrade || '').trim().toUpperCase();
    return gradeBands.some(band => {
      const b = String(band).trim().toUpperCase();
      if (b === 'K-2' || b === 'K–2') return ['K', '1', '2'].includes(g);
      if (b === '3-5' || b === '3–5') return ['3', '4', '5'].includes(g);
      if (b === '6-8' || b === '6–8') return ['6', '7', '8'].includes(g);
      return b === g;
    });
  };

  const eligibleLearners = learners.filter(l => gradeMatchesBands(l.grade, camp?.grade_bands));

  const initialFormat = isK2 ? 'weekly_am' : (defaultFormat || 'full_week');
  const [format, setFormat] = useState(initialFormat);
  const [extendedCare, setExtendedCare] = useState('none');
  const [selectedLearnerIds, setSelectedLearnerIds] = useState(() => {
    // Auto-select only if there is exactly one eligible learner.
    return eligibleLearners.length === 1 ? new Set([eligibleLearners[0].id]) : new Set();
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset state when opening a new section
  useEffect(() => {
    if (!open) return;
    setFormat(isK2 ? 'weekly_am' : (defaultFormat || 'full_week'));
    setExtendedCare('none');
    setSelectedLearnerIds(learners.length === 1 ? new Set([learners[0].id]) : new Set());
  }, [open, section?.id, isK2, defaultFormat, learners]);

  const isWeekend = camp?.subcategory === 'Weekend Classes';
  const halfDayPrice = useMemo(
    () => isWeekend
      ? Number(section?.price_2_day_halfday ?? camp?.price_2_day_halfday ?? 115)
      : Number(section?.price_5_day_halfday ?? camp?.price_5_day_halfday ?? 245),
    [section, camp, isWeekend],
  );
  const fullDayPrice = useMemo(
    () => isWeekend
      ? Number(section?.price_2_day ?? camp?.price_2_day ?? 230)
      : Number(section?.price_5_day ?? camp?.price_5_day ?? 450),
    [section, camp, isWeekend],
  );

  const basePrice = format === 'full_week' ? fullDayPrice : halfDayPrice;
  const ecEligible = format === 'full_week' && isExtCareEligible(section);
  const ecAddon = ecEligible
    ? ({ none: 0, early: 50, late: 50, both: 100 }[extendedCare] || 0)
    : 0;
  const perLearner = basePrice + ecAddon;
  const learnerCount = selectedLearnerIds.size || 1;
  const total = perLearner * learnerCount;

  const toggleLearner = (id) => {
    setSelectedLearnerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddToCart = async () => {
    if (!section?.id) {
      toast.error('Pick a location first');
      return;
    }
    if (!user) {
      // Guest flow — single ATC, no learner_id. The cart page will collect
      // parent + child info before checkout.
      setSubmitting(true);
      try {
        await api.post(`/camps/${camp.id}/add-to-cart/`, {
          camp_section_id: section.id,
          format,
          extended_care: ecEligible ? extendedCare : 'none',
        });
        toast.success(`Added to cart · $${total.toFixed(0)}`);
        onAddedToCart?.();
        onClose?.();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not add to cart');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (selectedLearnerIds.size === 0) {
      toast.error('Pick at least one child');
      return;
    }
    setSubmitting(true);
    try {
      const results = await Promise.allSettled(
        Array.from(selectedLearnerIds).map((learnerId) =>
          api.post(`/camps/${camp.id}/add-to-cart/`, {
            camp_section_id: section.id,
            learner_id: learnerId,
            format,
            extended_care: ecEligible ? extendedCare : 'none',
          }),
        ),
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected');
      if (okCount > 0) {
        toast.success(`Added ${okCount} child${okCount === 1 ? '' : 'ren'} to cart`);
        onAddedToCart?.();
        onClose?.();
      }
      if (failed.length > 0) {
        const msg = failed[0].reason?.response?.data?.detail || 'Could not add some children';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!camp || !section) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="simplified-register-sheet"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Sparkles className="h-5 w-5 text-rsc-yellow" />
            {camp.name}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {section.location_name}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {section.start_date} → {section.end_date}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Format radios */}
        <div className="mt-2 space-y-2" data-testid="simplified-format-options">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Choose your format
          </p>
          {/* Half day AM — always available */}
          <FormatRadio
            checked={format === 'weekly_am'}
            onChange={() => setFormat('weekly_am')}
            label={FORMAT_LABELS.weekly_am.title}
            sub={FORMAT_LABELS.weekly_am.time}
            price={halfDayPrice}
            testid="simplified-format-weekly_am"
          />
          {/* Half day PM — hidden on K-2 (AM only) */}
          {!isK2 && (
            <FormatRadio
              checked={format === 'weekly_pm'}
              onChange={() => setFormat('weekly_pm')}
              label={FORMAT_LABELS.weekly_pm.title}
              sub={FORMAT_LABELS.weekly_pm.time}
              price={halfDayPrice}
              testid="simplified-format-weekly_pm"
            />
          )}
          {/* Full day — hidden on K-2 (no PM session offered) */}
          {!isK2 && (
            <FormatRadio
              checked={format === 'full_week'}
              onChange={() => setFormat('full_week')}
              label={FORMAT_LABELS.full_week.title}
              sub={FORMAT_LABELS.full_week.time}
              price={fullDayPrice}
              badge="Best value"
              testid="simplified-format-full_week"
            />
          )}
        </div>

        {/* Extended care — full day at eligible locations only */}
        {ecEligible && (
          <div className="mt-4 space-y-2 rounded-lg border border-white/[0.08] p-3" data-testid="simplified-ec-options">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Sunrise className="h-3 w-3" /> Extended care (optional)
            </div>
            <div className="grid grid-cols-2 gap-2">
              {EC_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setExtendedCare(opt.value)}
                  className={`rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                    extendedCare === opt.value
                      ? 'border-rsc-yellow bg-rsc-yellow/10 text-rsc-yellow'
                      : 'border-white/10 text-muted-foreground hover:border-white/20'
                  }`}
                  data-testid={`simplified-ec-${opt.value}`}
                >
                  <div className="font-semibold">{opt.label}</div>
                  <div className="text-[10px] opacity-70">{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Learner picker — only for authed users */}
        {user && (
          <div className="mt-4 space-y-2" data-testid="simplified-learner-picker">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Users className="h-3 w-3" /> Who is coming?
            </div>
            {learners.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                Add a child profile in your account first.
              </p>
            ) : (
              learners.map((l) => {
                const eligible = gradeMatchesBands(l.grade, camp?.grade_bands);
                return (
                  <label
                    key={l.id}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                      eligible
                        ? 'border-white/[0.08] cursor-pointer hover:border-white/20'
                        : 'border-white/[0.04] opacity-40 cursor-not-allowed'
                    }`}
                    data-testid={`simplified-learner-${l.id}`}
                  >
                    <Checkbox
                      checked={selectedLearnerIds.has(l.id)}
                      onCheckedChange={() => eligible && toggleLearner(l.id)}
                      disabled={!eligible}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{l.first_name} {l.last_name}</div>
                      <div className="text-xs text-muted-foreground">
                        Grade {l.grade}
                        {!eligible && <span className="ml-1">· Not eligible for this camp ({(camp?.grade_bands || []).join(', ')})</span>}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        )}

        {/* Total + CTA */}
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-white/[0.08] pt-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Total {user ? `· ${learnerCount} child${learnerCount === 1 ? '' : 'ren'}` : ''}
            </p>
            <p className="text-2xl font-extrabold text-rsc-yellow" data-testid="simplified-total">
              ${total.toFixed(0)}
            </p>
          </div>
          <Button
            size="lg"
            onClick={handleAddToCart}
            disabled={submitting}
            className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow/90 font-bold"
            data-testid="simplified-add-to-cart"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</>
            ) : (
              'Add to cart'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FormatRadio({ checked, onChange, label, sub, price, badge, testid }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`group relative w-full rounded-lg border px-3 py-3 text-left transition-all ${
        checked
          ? 'border-rsc-yellow bg-rsc-yellow/10'
          : 'border-white/[0.08] hover:border-white/20'
      }`}
      data-testid={testid}
    >
      {badge && (
        <Badge className="absolute -top-2 right-3 bg-emerald-500 text-white text-[9px] font-extrabold uppercase tracking-wider">
          {badge}
        </Badge>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={`grid place-items-center h-4 w-4 rounded-full border-2 transition-colors ${
              checked ? 'border-rsc-yellow' : 'border-white/30 group-hover:border-white/50'
            }`}
          >
            {checked && <span className="h-2 w-2 rounded-full bg-rsc-yellow" />}
          </span>
          <div>
            <div className="text-sm font-bold">{label}</div>
            <div className="text-[11px] text-muted-foreground">{sub}</div>
          </div>
        </div>
        <div className="text-base font-extrabold text-rsc-yellow">
          ${price}
        </div>
      </div>
    </button>
  );
}
